import { MeetingTypes } from '@llx/common';
import { Dispatch, useCallback, useMemo } from 'react';

import {
  CaseClusterFieldsFragment,
  CaseClusterFieldsFragmentDoc,
  CurrentMeetingDataContextProviderQueryDocument,
  CurrentMeetingDataContextProviderQueryQuery,
  GetHostFieldsDocument,
  GetHostFieldsQuery,
  HostFieldsWhereUniqueInput,
  MeetingAdministrativeStatus,
  MeetingFieldsFragmentDoc,
} from '@/@generated/codegen/API';
import {
  LoggedType,
  useUserAuthContext,
} from '@/components/AuthTokenContextProvider/UserAuthContext';
import { useApolloClientContext } from '@/components/CustomApolloProvider/CustomApolloProvider';
import { captureSentry } from '@/utils/helpers/sentryHelper';

type Args = {
  currentMeetingId: string | null;
  setCurrentMeetingId: Dispatch<string | null>;
};

export const FAKE_MEETING_ID = 'fake-meeting';
const FAKE_CASE_CLUSTER_ID = 'fake-case-cluster';
const FAKE_HOST_FIELDS_ID = 'fake-host-fields';

export const useCreateFakeMeetingUtils = ({ currentMeetingId, setCurrentMeetingId }: Args) => {
  const { apolloClient } = useApolloClientContext();
  const authContext = useUserAuthContext();
  const isInFakeMeeting = useMemo(() => {
    return currentMeetingId === FAKE_MEETING_ID;
  }, [currentMeetingId]);

  const createAndJoinFakeMeeting = useCallback(
    (patientCaseIds: string[]) => {
      if (authContext.status !== LoggedType.LOGGED_IN) {
        captureSentry({
          title: 'createAndJoinFakeMeeting called when user is not logged in',
        });
        return;
      }
      if (patientCaseIds.length === 0) {
        captureSentry({
          title: 'createAndJoinFakeMeeting called with empty patientCaseIds',
          detail: { patientCaseIds },
        });
        return;
      }
      const meetingCacheId = apolloClient.cache.identify({
        __typename: 'Meeting',
        id: FAKE_MEETING_ID,
      });
      const caseClusterCacheId = apolloClient.cache.identify({
        __typename: 'CaseCluster',
        id: FAKE_CASE_CLUSTER_ID,
      });

      apolloClient.cache.writeFragment({
        id: caseClusterCacheId,
        fragment: CaseClusterFieldsFragmentDoc,
        fragmentName: 'CaseClusterFields',
        data: {
          __typename: 'CaseCluster',
          id: FAKE_CASE_CLUSTER_ID,
          cases: {
            edges: patientCaseIds.map((id) => ({
              __typename: 'PatientCaseEdge',
              node: {
                __typename: 'PatientCase',
                id,
                creationDate: new Date(),
                legacyCaseId: id,
                name: `Fake Case ${id}`,
                purchaseOrderNbr: 'N/A',
                clientName: 'livelinx',
                client: null,
              },
            })),
            pageInfo: {
              hasNextPage: false,
              hasPreviousPage: false,
              startCursor: '',
              endCursor: '',
            },
          },
          isMock: true,
          name: 'Fake Case Cluster',
          createdAt: new Date(),
          updatedAt: new Date(),
        } satisfies CaseClusterFieldsFragment,
      });

      const caseCluster: CaseClusterFieldsFragment | null = apolloClient.cache.readFragment({
        id: caseClusterCacheId,
        fragment: CaseClusterFieldsFragmentDoc,
        fragmentName: 'CaseClusterFields',
      });

      if (!caseCluster) {
        captureSentry({
          title: 'Failed to read case cluster from cache',
          detail: { caseClusterCacheId },
        });
        return;
      }

      apolloClient.cache.writeQuery({
        query: GetHostFieldsDocument,
        variables: {
          where: {
            meetingId: FAKE_MEETING_ID,
          } satisfies HostFieldsWhereUniqueInput,
        },
        data: {
          getHostFields: {
            __typename: 'HostFields',
            id: FAKE_HOST_FIELDS_ID,
            meetingId: FAKE_MEETING_ID,
            currentSlide: 0,
            currentCase: patientCaseIds[0] || null,
            currentAssetID: null,
          },
        } satisfies GetHostFieldsQuery,
      });

      apolloClient.cache.writeQuery({
        query: CurrentMeetingDataContextProviderQueryDocument,
        variables: {
          where: { id: FAKE_MEETING_ID },
        },
        data: {
          getMeeting: {
            __typename: 'Meeting',
            id: FAKE_MEETING_ID,
            hostId: authContext.userID,
            caseClusterId: FAKE_CASE_CLUSTER_ID,
            createdAt: new Date(),
            updatedAt: new Date(),
            eventDate: new Date(),
            name: 'Fake Meeting',
            eventLocation: 'In my head',
            hostFullName: 'Fake Host',
            isArchived: false,
            isDemo: false,
            isInTesting: false,
            isMock: true,
            meetingNotes: '',
            meetingType: MeetingTypes.LIVE,
            purchaseOrderNbr: 'N/A',
            endDate: null,
            startDate: null,
            streamUrl: '',
            administrativeStatus: MeetingAdministrativeStatus.Approved,
            createdById: authContext.userID,
            caseCluster: caseCluster,
          },
        } satisfies CurrentMeetingDataContextProviderQueryQuery,
      });

      const fakeMeeting = apolloClient.cache.readFragment({
        id: meetingCacheId,
        fragment: MeetingFieldsFragmentDoc,
      });

      setCurrentMeetingId(FAKE_MEETING_ID);

      return fakeMeeting;
    },
    [apolloClient, authContext.status]
  );

  return { createAndJoinFakeMeeting, isInFakeMeeting };
};
